import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { useMemoizedFn } from 'ahooks';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getCountryCodeIntersection } from '@intelligence/utils/filterUtils';
import { LANGUAGE_EN } from '@home/_config/config';
import './index.scss';

export const formatRegionOptions = (options: any[]) => {
  const regionCode: string[] = [];
  const countryCode: string[] = [];
  if (!Array.isArray(options)) {
    return {
      countryCode,
      regionCode,
      regionOptions: [],
    };
  }
  const regionOptions = options.map((item: any) => {
    const {
      country_set = [],
      area_code: regionValue,
      area_name_ch: textCN,
      area_name_en: text,
    } = item;
    regionCode.push(item?.area_code);
    const children =
      country_set?.map((subItem: any) => {
        const {
          country_code: countryValue,
          country_name_ch: textCN,
          country_name_en: text,
        } = subItem;
        countryCode.push(countryValue);
        return {
          ...subItem,
          value: countryValue,
          icon: getCountryFlag(countryValue),
          text,
          textCN,
        };
      }) || [];
    return {
      ...item,
      value: regionValue,
      text,
      textCN,
      children,
    };
  });
  return {
    countryCode,
    regionCode,
    regionOptions,
  };
};

export default (props: any) => {
  const {
    isTopCompanies = false,
    value: valueProp,
    defaultValue,
    options,
    onConfirm,
    disableNoChina,
    globalInfo,
    isHiddenGlobal=false,
    isHiddenGlobalChina=false,
    ...restProps
  } = props;

  const { formatMessage } = useIntl();
  const [value, setValue] = useState({
    type: 'market',
    regions: [],
    markets: ['global'],
  });
  const [allMarkets, setAllMarkets] = useState<string[]>([]);
  const [allRegions, setAllRegions] = useState<string[]>([]);
  const [formatOptions, setformatOptions] = useState<any[]>([]);

  useEffect(() => {
    const { regionCode, countryCode, regionOptions } =
      formatRegionOptions(options);
    setAllMarkets(countryCode);
    setAllRegions(regionCode);
    setformatOptions(regionOptions);
  }, [JSON.stringify(options)]);

  const getGlobalTips = () => {
    if (isTopCompanies) return formatMessage({ id: 'dbi_info_global_filter3' });
    if (globalInfo) return globalInfo;
    return formatMessage({ id: `dbi_info_global_filter` });
  };

  const radioOptions = useMemo(() => {
    const dataSource = [
      {
        value: 'global',
        text: 'Global',
        textCN: '全球',
        isHidden: isHiddenGlobal,
        infoIcon: globalInfo !== null && (
          <TipContext
            AWidth={600}
            className="dbg-regionselector-global-tip"
            tipText={getGlobalTips()}
          />
        ),
      },
      {
        value: 'global_wo_china',
        text: 'Global except China mainland',
        textCN: '全球（不包括中国大陆）',
        isHidden: isHiddenGlobalChina,
        disabled: disableNoChina,
      },
    ].filter(task => !task.isHidden);
    return dataSource;
  }, [isHiddenGlobal, globalInfo, disableNoChina, isHiddenGlobalChina])

  const getMarketsValue = (markets: string[]) => {
    if (Array.isArray(markets) && markets.length === 1) {
      if (markets[0] === 'global') {
        /**获取全量market */
        return allMarkets || [];
      } else if (markets[0] === 'global_wo_china') {
        /**获取除cn外的market */
        return allMarkets.filter((t) => {
          return t !== 'cn';
        });
      } else {
        return markets;
      }
    }
    return markets;
  };

  const onCloseReginSelect = (value: any) => {
    const realMarkets = getMarketsValue(value?.markets) || [];
    setValue(value);
    if (onConfirm instanceof Function) {
      onConfirm({ ...value, realMarkets: realMarkets });
    }
  };

  useEffect(() => {
    const markets: string[] = valueProp?.markets || [];
    if (!Array.isArray(formatOptions) || !formatOptions.length) {
      return;
    }

    let filterMarkets = getCountryCodeIntersection({
      markets: markets,
      countriesMarkets: allMarkets,
      disableNoChina: disableNoChina,
      defaultMarkets: defaultValue?.markets ?? ['global'],
    });
    if (
      filterMarkets?.length === 1 &&
      ['global', 'global_wo_china'].includes(filterMarkets[0])
    ) {
      setValue({
        type: 'market',
        regions: [],
        markets: filterMarkets,
      });
      return;
    }

    let areaCodes: string[] = [];
    let scopeType = 'region';
    formatOptions.forEach((item) => {
      // 判断 item 的子项是否全部在 filterMarkets 中
      const allChildrenIncluded = item?.children?.every((subItem) =>
        filterMarkets?.includes(subItem?.country_code),
      );
      // 判断 item 的子项是否全部不在 filterMarkets 中
      const allChildrenExcluded = item?.children?.every(
        (subItem) => !filterMarkets?.includes(subItem?.country_code),
      );

      // 如果 item 的所有子项都在 filterMarkets 中，将其 area_code 添加到 regions 数组中
      if (allChildrenIncluded) {
        areaCodes.push(item.area_code);
      }
      // 如果 allChildrenIncluded  和 allChildrenExcluded  都为 false，说明 item 的子项部分在 filterMarkets 中，此时将 type 设为 'market'
      if (!allChildrenIncluded && !allChildrenExcluded) {
        scopeType = 'market';
      }
    });

    setValue({
      type: scopeType,
      regions: areaCodes,
      markets: filterMarkets ?? [],
    });
  }, [JSON.stringify(valueProp), allMarkets, formatOptions]);

  return (
    <>
      <NewRegionSelect
        isTopCompanies={isTopCompanies}
        popularList={[]}
        className="dbg-region-dropdown"
        boxClassName="dbg-region-dropdown-box"
        value={value}
        options={formatOptions}
        onCloseReginSelect={(val) => {
          const currentMarkets = val?.markets || [];
          const preMarkets = value?.markets || [];
          if (JSON.stringify(currentMarkets) === JSON.stringify(preMarkets)) {
            //没有任何操作时不调用接口
            return;
          }
          /**feat: 美国特殊处理了只有一个时返回的是美国而不是地区，因此，这里判断如果地区只有一个且只选了一个地区，则为market格式输出 */
          let changeVal = val;
          if (val?.type === 'region') {
            if (val?.markets?.length === 1 && val?.regions?.length === 1) {
              changeVal = ({
                ...val || {},
                type: 'market',
                markets: val?.markets,
                regions: val?.markets
              });
            }
            if (val?.markets?.length === 1 && val.markets[0] === 'global') {
              changeVal = {
                ...val || {},
                type: 'market',
                markets: ['global'],
                regions: [],
              };
            }
          }
          onCloseReginSelect(changeVal);
        }}
        showRadioOptionsTips={false}
        radioOptions={radioOptions}
        {...restProps}
        locale={LANGUAGE_EN}
      />
    </>
  );
};
